import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Modal, Pill, Spinner, EmptyState, Field, ChipRow } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { CheckCircle2, Info, Trash2, UserPlus } from "lucide-react";

const ROLES = [
  { key: "admin", label: "Admin", hint: "Full access to everything" },
  { key: "sampling", label: "Sampling Team", hint: "Sampling meetings + sampling/initial quotes" },
  { key: "production", label: "Production Team", hint: "Production meetings + production/initial quotes" },
];
const TONE = { admin: "brand", sampling: "success", production: "warning" };

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") === "invites" ? "invites" : "members");

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("sampling");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, a] = await Promise.all([api.get("/users"), api.get("/allowed-emails")]);
      setUsers(u); setAllowed(a);
    } catch (e) {
      if (String(e?.message || "").toLowerCase().includes("admin")) { alert("Admins only"); navigate(-1); }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (role) => {
    if (!editing) return;
    try {
      const updated = await api.patch(`/users/${editing.user_id}`, { role });
      setUsers((all) => all.map((u) => u.user_id === updated.user_id ? updated : u));
      setEditing(null);
    } catch (e) { alert(e?.message); }
  };

  const addAllowed = async () => {
    const e = inviteEmail.trim().toLowerCase();
    if (!e.includes("@")) { alert("Enter a valid email"); return; }
    try {
      setInviting(true);
      await api.post("/allowed-emails", { email: e, role: inviteRole });
      setInviteEmail("");
      load();
    } catch (err) { alert(err?.message); } finally { setInviting(false); }
  };

  const removeAllowed = async (email) => {
    if (!window.confirm(`Revoke access for ${email}?`)) return;
    await api.delete(`/allowed-emails/${encodeURIComponent(email)}`);
    load();
  };

  if (loading) return <><AppHeader title="Team" back /><div className="sg-main"><Spinner /></div></>;

  return (
    <>
      <AppHeader title="Team" back />
      <div className="sg-main sg-fade-in">
      <div style={{ marginBottom: 20 }}>
        <ChipRow
          items={[{ key: "members", label: "Members" }, { key: "invites", label: "Allowed emails" }]}
          value={tab} onChange={setTab}
        />
      </div>

      {tab === "members" ? (
        <>
          <div className="sg-card" style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16, background: "var(--surface-subtle)", borderColor: "var(--surface-subtle)" }}>
            <Info size={18} color="var(--brand)" />
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>
              Team members appear here after their first Google sign-in. Click a member to change their role.
            </div>
          </div>

          {users.length === 0 ? <EmptyState title="No team members yet" /> : (
            users.map((u) => {
              const isSelf = u.user_id === user?.user_id;
              return (
                <div key={u.user_id} className="sg-card" style={{ marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }} onClick={() => setEditing(u)} data-testid={`team-row-${u.user_id}`}>
                  <div style={{ width: 40, height: 40, borderRadius: 20, background: "var(--surface-subtle)", display: "grid", placeItems: "center", color: "var(--brand)", fontWeight: 800 }}>
                    {(u.name || u.email).charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 700 }}>{u.name}</span>
                      {isSelf && <span style={{ fontSize: 9, color: "var(--brand)", background: "var(--surface-subtle)", padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{u.email}</div>
                  </div>
                  <Pill label={u.role} tone={TONE[u.role] || "neutral"} />
                </div>
              );
            })
          )}
        </>
      ) : (
        <>
          <div className="sg-card" style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16, background: "var(--surface-subtle)", borderColor: "var(--surface-subtle)" }}>
            <Info size={18} color="var(--brand)" />
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>
              Only listed emails can sign in. You ({user?.email}) are always allowed as an admin.
            </div>
          </div>

          <div className="sg-section-h" style={{ marginTop: 0 }}>Invite</div>
          <Field placeholder="teammate@studiogrape.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="allow-email-input" />
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {ROLES.map((r) => (
              <button key={r.key} className={`sg-chip ${inviteRole === r.key ? "sg-chip-active" : ""}`} onClick={() => setInviteRole(r.key)} data-testid={`allow-role-${r.key}`} style={{ flex: 1 }}>{r.label}</button>
            ))}
          </div>
          <button className="sg-btn sg-btn-primary" onClick={addAllowed} disabled={inviting} data-testid="allow-add" style={{ marginBottom: 24 }}>
            <UserPlus size={15} /> {inviting ? "Adding..." : "Add email"}
          </button>

          <div className="sg-section-h">Allowed list ({allowed.length})</div>
          {allowed.length === 0 ? <EmptyState title="No staff allow-listed yet" hint="Add teammate emails above." /> : (
            allowed.map((it) => (
              <div key={it.email} className="sg-card" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{it.email}</div>
                  {it.invited_by && <div style={{ fontSize: 11, color: "var(--muted)" }}>Invited by {it.invited_by}</div>}
                </div>
                <Pill label={it.role} tone={TONE[it.role] || "neutral"} />
                <button onClick={() => removeAllowed(it.email)} className="sg-btn" style={{ padding: 8 }} data-testid={`allow-remove-${it.email}`}><Trash2 size={14} color="var(--danger)" /></button>
              </div>
            ))
          )}
        </>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Change role">
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>{editing?.name} · {editing?.email}</div>
        {ROLES.map((r) => {
          const active = editing?.role === r.key;
          return (
            <button key={r.key} onClick={() => changeRole(r.key)} disabled={active}
              style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%", padding: 14, marginBottom: 8,
                borderRadius: 10, border: `1px solid ${active ? "var(--brand)" : "var(--line)"}`,
                background: active ? "var(--brand)" : "var(--surface)",
                color: active ? "#fff" : "var(--ink)", cursor: active ? "default" : "pointer", textAlign: "left",
              }}
              data-testid={`pick-role-${r.key}`}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{r.label}</div>
                <div style={{ fontSize: 12, color: active ? "rgba(255,255,255,0.75)" : "var(--muted)" }}>{r.hint}</div>
              </div>
              {active && <CheckCircle2 size={18} />}
            </button>
          );
        })}
      </Modal>
      </div>
    </>
  );
}
